import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Tooltip,
  Checkbox,
  Divider,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Grid
} from "@mui/material";
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Block,
  QuestionMark,
  ChevronLeft,
  ChevronRight,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelCircleIcon,
  Error as ErrorIcon,
  RemoveCircle as RemoveCircleIcon,
  Inventory as InventoryIcon,
  Info as InfoIcon,
  History as HistoryIcon,
  LocalPharmacy as LocalPharmacyIcon,
  FiberManualRecord,
  Event as EventIcon,
  Description as DescriptionIcon,
  CloudUpload as CloudUploadIcon,
  Edit as EditIcon,
  Medication as MedicationIcon
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import SignatureInput from "../components/SignatureInput";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addMonths,
  subMonths,
  isFuture,
  isToday,
  isBefore,
  startOfDay,
} from "date-fns";
import { formatToUIDate, formatToBackendDateTime } from "../utils/dateUtils";
import OrderMedicationService from "../services/OrderMedicationService";
import residentService from "../services/residentService";
import clinicalNoteService from "../services/clinicalNoteService";
import { MonthlyMARRecord, MonthlyMARSlot, MonthlyMARDose } from "../types/medication.types";
import { useAuth } from "../contexts/AuthContext";
import userService from "../services/userService";
import { UserDTO } from "../types/user.types";

const STAT_REGISTRY_KEY = "qt-carex-stat-dose-medications";

const normalizeMedName = (value: any): string => String(value ?? "").trim().toLowerCase();

const isStatDoseSource = (entity: any): boolean => {
  if (!entity || typeof entity !== "object") return false;

  const sourceLike = String(
    entity.source ??
    entity.medicationSource ??
    entity.orderSource ??
    entity.type ??
    entity.orderType ??
    entity.medicationType ??
    "",
  ).toLowerCase();

  const scheduleLike = String(
    entity.schedule ??
    entity.frequency ??
    entity.reason ??
    "",
  ).trim().toLowerCase();

  return Boolean(
    entity.isStatDose === true ||
    entity.statDose === true ||
    entity.isStat === true ||
    sourceLike === "stat_dose" ||
    sourceLike === "stat-dose" ||
    sourceLike === "statdose" ||
    scheduleLike === "stat" ||
    scheduleLike === "stat dose" ||
    scheduleLike === "stat_dose" ||
    scheduleLike === "stat-dose" ||
    scheduleLike.includes("stat"),
  );
};

interface MedicationAdministrationRecordProps {
  residentId: string;
  onClose?: () => void;
  onSaveSuccess?: () => void;
  refreshTrigger?: number;
  showResidentName?: boolean;
  sx?: any;
  readOnly?: boolean;
  medicationDefinitions?: any[];
}

const MedicationAdministrationRecord: React.FC<
  MedicationAdministrationRecordProps
> = ({
  residentId,
  onClose,
  onSaveSuccess,
  refreshTrigger,
  showResidentName = true,
  sx,
  readOnly = false,
  medicationDefinitions = [],
}) => {
    const { user } = useAuth();

    const canEditPast = useMemo(() => {
      const allowedRoles = ["ADMIN", "FACILITY_ADMIN", "SUPER_ADMIN", "CLIENT_ADMIN", "OFFICER"];
      const normalizeRole = (r: any) => {
        if (!r) return "";
        return String(r).toUpperCase().replace(/^ROLE_/, "").replace(/[-\s]+/g, "_");
      };

      const currentRole = normalizeRole(user?.role || (user as any)?.userRole);
      if (allowedRoles.includes(currentRole)) return true;

      if (Array.isArray((user as any)?.roles)) {
        if ((user as any).roles.some((r: any) => allowedRoles.includes(normalizeRole(r)))) {
          return true;
        }
      }

      try {
        const stored = localStorage.getItem('user');
        if (stored) {
          const parsed = JSON.parse(stored);
          const storedRole = normalizeRole(parsed?.role || parsed?.userRole);
          if (allowedRoles.includes(storedRole)) return true;
          if (Array.isArray(parsed?.roles) && parsed.roles.some((r: any) => allowedRoles.includes(normalizeRole(r)))) {
            return true;
          }
        }
      } catch (e) {}

      return false;
    }, [user]);

    const [currentDate, setCurrentDate] = useState(new Date());
    const [medicationRecords, setMedicationRecords] = useState<
      MonthlyMARRecord[]
    >([]);
    const [nurseNotes, setNurseNotes] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [residentName, setResidentName] = useState("");
    const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
    const [selectedCell, setSelectedCell] = useState<{
      record: MonthlyMARRecord;
      slot: MonthlyMARSlot;
      day: Date;
      doseData?: MonthlyMARDose;
    } | null>(null);
    const [isSavingStatus, setIsSavingStatus] = useState(false);
    const [resultsResponse, setResultsResponse] = useState("");
    const [administrationTime, setAdministrationTime] = useState(format(new Date(), "HH:mm"));
    const [dialogStatus, setDialogStatus] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [pendingStatus, setPendingStatus] = useState<string | null>(null);

    const normalize = (s: string) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "").trim();

    // Aggressive catch-all check for any property containing 'narcotic'
    const checkNarcotic = (obj: any) => {
      if (!obj) return false;
      return Object.keys(obj).some(key => {
        const k = key.toLowerCase();
        return (k.includes('narcotic') || k.includes('norcatic')) &&
          (obj[key] === true || obj[key] === "true" || obj[key] === 1 || obj[key] === "1");
      });
    };

    // Aggressive catch-all check for any property containing 'critical'
    const checkCritical = (obj: any) => {
      if (!obj) return false;
      return Object.keys(obj).some(key => {
        const k = key.toLowerCase();
        return k.includes('critical') &&
          (obj[key] === true || obj[key] === "true" || obj[key] === 1 || obj[key] === "1");
      });
    };

    // Inline Signature States
    const [signatureImage, setSignatureImage] = useState<string | null>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasDrawn, setHasDrawn] = useState(false);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [signatureTab, setSignatureTab] = useState(0); // 0 for draw, 1 for upload
    const [signatureFile, setSignatureFile] = useState<Blob | File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [snackbar, setSnackbar] = useState<{
      open: boolean;
      message: string;
      severity: "success" | "error" | "warning" | "info";
    }>({
      open: false,
      message: "",
      severity: "success",
    });
    const [statMedications, setStatMedications] = useState<Set<string>>(new Set());
    const [narcoticMedNames, setNarcoticMedNames] = useState<Set<string>>(new Set());
    const [criticalMedNames, setCriticalMedNames] = useState<Set<string>>(new Set());
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);

    // ── Narcotic Witness Verification States (matching MedicationSchedulePage) ──
    const [narcoticDialogOpen, setNarcoticDialogOpen] = useState(false);
    const [otherCaregivers, setOtherCaregivers] = useState<UserDTO[]>([]);
    const [caregiversLoading, setCaregiversLoading] = useState(false);
    const [selectedWitnessId, setSelectedWitnessId] = useState<string>('');
    const [witnessSignatureDialogOpen, setWitnessSignatureDialogOpen] = useState(false);
    const [witnessSignatureData, setWitnessSignatureData] = useState<string | null>(null);
    const [mySignatureDialogOpen, setMySignatureDialogOpen] = useState(false);
    const [adminSignatureData, setAdminSignatureData] = useState<string | null>(null);
    const [adminSignatureMode, setAdminSignatureMode] = useState<'upload' | 'digital' | null>(null);
    const [witnessSignatureMode, setWitnessSignatureMode] = useState<'upload' | 'digital' | null>(null);
    const [myProfileSignature, setMyProfileSignature] = useState<string | null>(() =>
      user?.id ? localStorage.getItem(`user_signature_${user.id}`) : null
    );
    const [residentFacilityId, setResidentFacilityId] = useState<string | null>(null);

    const adminUploadRef = useRef<HTMLInputElement>(null);
    const witnessUploadRef = useRef<HTMLInputElement>(null);

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>, setSignatureData: (data: string) => void) => {
      const file = event.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          setSignatureData(reader.result);
        }
      };
      reader.readAsDataURL(file);
      event.target.value = '';
    };

    useEffect(() => {
      setMyProfileSignature(user?.id ? localStorage.getItem(`user_signature_${user.id}`) : null);
    }, [user?.id]);

    const dataURLtoFile = (dataurl: string, filename: string) => {
      const arr = dataurl.split(',');
      const mimeMatch = arr[0].match(/:(.*?);/);
      const mime = mimeMatch ? mimeMatch[1] : '';
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    };

    const handleSignatureSave = async (signatureDataUrl: string) => {
      if (pendingStatus) {
        const file = dataURLtoFile(signatureDataUrl, 'signature.png');
        await submitRecord(pendingStatus, file);
        setSignatureModalOpen(false);
      }
    };

    const readStatMedicationNames = (residentKey: string) => {
      try {
        const raw = localStorage.getItem(STAT_REGISTRY_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        const keys = [`resident::${residentKey}`, `unknown::${residentKey}`];
        const names = keys.flatMap((k) => (parsed && parsed[k]) || []);
        return new Set((names || []).map(normalizeMedName).filter(Boolean));
      } catch {
        return new Set<string>();
      }
    };

    const isStatMedication = (record: any) =>
      isStatDoseSource(record) || statMedications.has(normalizeMedName(record?.medicationName));

    const isNarcoticMedication = (record: any) => {
      if (!record) return false;
      // Direct flag on the MAR record itself
      if (record.isNarcotic === true || record.isNarcotic === "true") return true;
      // Pre-scanned set from medicationDefinitions
      if (narcoticMedNames.has(normalize(record.medicationName || ""))) return true;
      // Aggressive key-scan fallback
      return checkNarcotic(record);
    };

    const isCriticalMedication = (record: any) => {
      if (!record) return false;
      // Direct flag on the MAR record itself
      if (record.isCritical === true || record.isCritical === "true") return true;
      // Pre-scanned set from medicationDefinitions
      if (criticalMedNames.has(normalize(record.medicationName || ""))) return true;
      // Aggressive key-scan fallback
      return checkCritical(record);
    };

    useEffect(() => {
      if (residentId) {
        setStatMedications(readStatMedicationNames(residentId));
        fetchData();
      }
    }, [residentId, currentDate, refreshTrigger]);

    // Pre-calculate narcotic medications from definitions for reliable lookup
    useEffect(() => {
      const normalize = (s: string) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "").trim();
      const names = new Set<string>();

      medicationDefinitions.forEach(m => {
        const isNarc = !!(m.isNarcotic || m.isNorcatiik || m.isNorcatick || m.is_narcotic || checkNarcotic(m));
        if (isNarc) {
          const normalizedName = normalize(m.medicationName || m.name);
          if (normalizedName) names.add(normalizedName);
        }
      });

      console.log("MAR: Pre-scanned narcotics count:", names.size, Array.from(names));
      setNarcoticMedNames(names);
    }, [medicationDefinitions]);

    // Pre-calculate critical medications from definitions for reliable lookup
    useEffect(() => {
      const normalize = (s: string) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "").trim();
      const names = new Set<string>();

      medicationDefinitions.forEach(m => {
        const isCrit = !!(m.isCritical || m.is_critical || checkCritical(m));
        if (isCrit) {
          const normalizedName = normalize(m.medicationName || m.name);
          if (normalizedName) names.add(normalizedName);
        }
      });

      console.log("MAR: Pre-scanned critical medications count:", names.size, Array.from(names));
      setCriticalMedNames(names);
    }, [medicationDefinitions]);

    // Signature Helpers
    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
      setFormError(null);
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const rect = canvas.getBoundingClientRect();
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#000000';
      ctx.beginPath();
      ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
      setIsDrawing(true);
      setHasDrawn(true);
    };

    const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!isDrawing) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const rect = canvas.getBoundingClientRect();
      ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
      ctx.stroke();
    };

    const stopDrawing = () => {
      if (!isDrawing) return;
      setIsDrawing(false);

      const canvas = canvasRef.current;
      if (canvas && hasDrawn) {
        const dataUrl = canvas.toDataURL('image/png');
        setSignatureImage(dataUrl);
        const file = dataURLtoFile(dataUrl, 'signature.png');
        setSignatureFile(file);
      }
    };

    const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
      setFormError(null);
      const touch = e.touches[0];
      if (!touch) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const rect = canvas.getBoundingClientRect();
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#000000';
      ctx.beginPath();
      ctx.moveTo(touch.clientX - rect.left, touch.clientY - rect.top);
      setIsDrawing(true);
      setHasDrawn(true);
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
      if (!isDrawing) return;
      const touch = e.touches[0];
      if (!touch) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const rect = canvas.getBoundingClientRect();
      ctx.lineTo(touch.clientX - rect.left, touch.clientY - rect.top);
      ctx.stroke();
    };

    const clearCanvas = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignatureImage(null);
      setSignatureFile(null);
      setHasDrawn(false);
      setFormError(null);
    };

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormError(null);
      const file = event.target.files?.[0];
      if (file) {
        setSignatureFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setSignatureImage(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    };

    const resetSignatureState = () => {
      setPendingStatus(null);
      setSignatureImage(null);
      setSignatureFile(null);
      setSignatureTab(0);
      setHasDrawn(false);
      setFormError(null);
      setNarcoticDialogOpen(false);
      setSelectedWitnessId('');
      setWitnessSignatureData(null);
      setWitnessSignatureMode(null);
      setAdminSignatureData(null);
      setAdminSignatureMode(null);
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
      }
    };

    // Fetch other caregivers for the narcotic witness dropdown
    useEffect(() => {
      const effectiveFacilityId = residentFacilityId
        || (Array.isArray(user?.facility) ? user?.facility[0]?.id : (user?.facility as any)?.id)
        || user?.facilityId
        || user?.tenantId;

      const fetchCaregivers = async () => {
        setCaregiversLoading(true);
        try {
          let caregivers: UserDTO[] = [];
          if (effectiveFacilityId) {
            try {
              caregivers = await userService.getCaregiversByFacility(effectiveFacilityId);
            } catch (e) {
              console.warn("Failed to get caregivers by facility, trying active users", e);
            }
          }
          if (!caregivers || caregivers.length === 0) {
            caregivers = await userService.getActiveUsers();
          }

          const isCurrentUser = (u: UserDTO): boolean => {
            if (u.keycloakId && u.keycloakId === user?.id) return true;
            if (u.id && u.id === user?.id) return true;
            if (u.username && user?.username && u.username === user.username) return true;
            return false;
          };

          setOtherCaregivers((caregivers || []).filter((u) => !isCurrentUser(u)));
        } catch (err) {
          console.error("Failed to fetch caregivers for narcotic witness:", err);
        } finally {
          setCaregiversLoading(false);
        }
      };

      fetchCaregivers();
    }, [residentFacilityId, user]);

    const fetchData = async () => {
      setLoading(true);
      try {
        // We need the facilityId to call the MAR API.
        // Assuming residentService can provide the resident details which contains facilityId.
        const resident = await residentService.getResidentById(residentId);
        const facilityId = resident.facilityId;
        setResidentFacilityId(facilityId || null);
        setResidentName(residentService.getFullName(resident));

        if (!facilityId) {
          console.error("Facility ID not found for resident");
          setLoading(false);
          return;
        }

        const start = startOfMonth(currentDate);
        const end = endOfMonth(currentDate);
        // Format dates for API
        const startDateStr = format(start, "yyyy-MM-dd");
        const endDateStr = format(end, "yyyy-MM-dd");

        // Updated to use the correct GET API for monthly statuses
        const response = await OrderMedicationService.getMonthlyMAR(
          facilityId,
          residentId,
          startDateStr,
          endDateStr,
        );
        setMedicationRecords(response);

        const notes = await clinicalNoteService.getNurseNotesByResident(residentId);
        setNurseNotes(notes || []);
      } catch (error) {
        console.error("Failed to fetch MAR data", error);
      } finally {
        setLoading(false);
      }
    };

    const handleLowStockToggle = async (record: MonthlyMARRecord, checked: boolean) => {
      try {
        const now = new Date();
        const slotTime = record.doses?.[0]?.slotTime || "";

        let existingNoteId: string | undefined = undefined;

        // Find if there's already a note for this medication today and this slot
        const existingNote = nurseNotes.find((n: any) => {
          if (!n.dateTime) return false;
          const d = new Date(n.dateTime);
          const isSameDay = d.getFullYear() === now.getFullYear() &&
            d.getMonth() === now.getMonth() &&
            d.getDate() === now.getDate();

          if (n.medicationName !== record.medicationName) return false;
          if (!isSameDay) return false;

          if (n.slotTime && slotTime) {
            return n.slotTime.startsWith(slotTime.substring(0, 5));
          }
          return false;
        });

        existingNoteId = existingNote?.id;

        const payload = {
          residentId: residentId,
          medicationName: record.medicationName,
          dosage: record.dose || "N/A",
          dateTime: formatToBackendDateTime(now) || "",
          slotTime: slotTime,
          medicationStatus: existingNote?.medicationStatus || "Note",
          reason: record.schedule || "Stock Update",
          schedule: record.schedule || "Stock Update",
          result: checked ? "Marked as Low Stock" : "Marked as In Stock",
          lowStock: checked,
          updatedStock: record.updatedStock ?? (record as any).updated_stock ?? "N/A"
        };

        if (existingNoteId) {
          await clinicalNoteService.updateNurseNote(existingNoteId, payload);
        } else {
          await clinicalNoteService.createNurseNote(payload);
        }

        // Refresh nurse notes to update the UI
        const notes = await clinicalNoteService.getNurseNotesByResident(residentId);
        setNurseNotes(notes || []);
        if (onSaveSuccess) onSaveSuccess();
      } catch (error) {
        console.error("Failed to update low stock status", error);
      }
    };

    const handleCellClick = (record: MonthlyMARRecord, slot: MonthlyMARSlot, day: Date, doseData?: MonthlyMARDose) => {
      if (readOnly) return;
      const isPastDay = isBefore(day, startOfDay(new Date()));
      const isFutureDay = isFuture(day) && !isToday(day);

      if (!doseData || isFutureDay || doseData.status === "Taken") return;

      if (isPastDay) {
        if (!canEditPast) {
          return;
        }
      }

      resetSignatureState();
      setSelectedCell({ record, slot, day, doseData });
      const initialNote = doseData?.notes || "";
      setResultsResponse((initialNote.startsWith("Administered as ") || initialNote.startsWith("Scheduled Dose")) ? "" : initialNote);
      // Default to the current real-world time for the administration entry
      const defaultTime = format(new Date(), "HH:mm");
      setAdministrationTime(defaultTime);
      setDialogStatus(doseData?.status || null);
      setIsStatusDialogOpen(true);
    };


    const checkIsReasonMandatory = (
      day: Date | string | null | undefined,
      slotTimeStr?: string | null,
      dateTimeStr?: string | null
    ): boolean => {
      if (!day) return false;
      const targetDate = typeof day === "string" ? new Date(day) : new Date(day.getTime());
      if (isNaN(targetDate.getTime())) return false;

      const now = new Date();
      const todayStart = startOfDay(now);
      const cellDayStart = startOfDay(targetDate);

      // 1. Past date -> Reason mandatory for any authorized user
      if (isBefore(cellDayStart, todayStart)) {
        return true;
      }

      // 2. Today -> check if past active window
      if (cellDayStart.getTime() === todayStart.getTime()) {
        let effectiveSlotTime = slotTimeStr;
        if (!effectiveSlotTime && dateTimeStr) {
          try {
            const dt = new Date(dateTimeStr);
            if (!isNaN(dt.getTime())) {
              effectiveSlotTime = format(dt, "HH:mm");
            }
          } catch {
            // ignore
          }
        }

        if (!effectiveSlotTime) return false;

        try {
          let hours = 0;
          let minutes = 0;

          const upper = effectiveSlotTime.trim().toUpperCase();
          const isPM = upper.includes("PM");
          const isAM = upper.includes("AM");
          const cleanTime = upper.replace(/(AM|PM)/g, "").trim();
          const parts = cleanTime.split(":").map(Number);

          if (parts.length >= 1 && !isNaN(parts[0])) {
            hours = parts[0];
            minutes = parts.length >= 2 && !isNaN(parts[1]) ? parts[1] : 0;
            if (isPM && hours < 12) hours += 12;
            if (isAM && hours === 12) hours = 0;
          }

          const slotDateTime = new Date();
          slotDateTime.setHours(hours, minutes, 0, 0);

          // If scheduled time has not arrived yet, it's on-time / upcoming
          if (now <= slotDateTime) {
            return false;
          }

          // Scheduled time HAS passed. Check active window end.
          let windowEndHour = hours + 2; // fallback 2-hour buffer
          if (hours >= 6 && hours < 11) windowEndHour = 11;
          else if (hours >= 11 && hours < 15) windowEndHour = 15;
          else if (hours >= 15 && hours < 19) windowEndHour = 19;
          else if (hours >= 19 || hours < 6) windowEndHour = 24;

          const currentHourFloat = now.getHours() + (now.getMinutes() / 60);

          // If current time is past active window end -> Late -> Mandatory Reason
          if (currentHourFloat >= windowEndHour) {
            return true;
          }
        } catch (e) {
          console.error("Error evaluating active window:", e);
        }
      }

      return false;
    };

    const handleRecordStatus = async (status: string) => {
      if (!selectedCell) return;
      setFormError(null);

      const isReasonReq = checkIsReasonMandatory(selectedCell.day, selectedCell.slot.slotTime, selectedCell.doseData?.dateTime);
      if ((isReasonReq || status === 'Unable' || status === 'Missed' || status === 'NotTaken') && !resultsResponse.trim()) {
        const labelMap: Record<string, string> = {
          NotTaken: 'Missed',
          Missed: 'Missed',
          OutOfStock: 'Out of Stock',
          OutOfMeds: 'Out of Stock',
          Unable: 'Unable'
        };
        const statusLabel = labelMap[status] || status;
        setFormError(isReasonReq ? "Reason is mandatory for late or past-date medication administration." : `Results / Response is required for ${statusLabel}.`);
        return;
      }

      const r = selectedCell.record as any || {};
      const d = selectedCell.doseData as any || {};

      const normalize = (s: string) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "").trim();
      const medNameNormalized = normalize(r.medicationName);

      // 1. Direct flags on record or dose
      const hasDirectFlag = !!(
        r.isNarcotic || r.is_narcotic || r.isNorcatiik || r.isNorcatick ||
        d.isNarcotic || d.is_narcotic || d.isNorcatiik || d.isNorcatick ||
        checkNarcotic(r) || checkNarcotic(d)
      );

      // 2. Lookup in our pre-scanned narcotic list (Most reliable)
      const isNarcoticInList = medNameNormalized ? narcoticMedNames.has(medNameNormalized) : false;

      const isNarcoticMed = hasDirectFlag || isNarcoticInList;

      console.log("MAR DETECTOR DEBUG:", {
        medName: r.medicationName,
        normalized: medNameNormalized,
        isNarcoticMed,
        hasDirectFlag,
        isNarcoticInList,
        scannedCount: narcoticMedNames.size
      });

      if (status === "Taken" && isNarcoticMed) {
        setPendingStatus(status);
        setSelectedWitnessId('');
        setWitnessSignatureData(null);
        setWitnessSignatureMode(null);
        setAdminSignatureData(null);
        setAdminSignatureMode(null);
        setNarcoticDialogOpen(true);
        console.log("STOP: Narcotic detected. Opening Narcotic Verification dialog.");
        return; // Stop here and show narcotic verification dialog
      } else if (status === "Taken" && !isNarcoticMed) {
        console.warn("PROCEED: No narcotic flag found. Calling API directly.");
      }

      // ONLY for non-narcotic or non-Taken status do we call the API
      await submitRecord(status);
      setIsStatusDialogOpen(false);
    };

    const handleNarcoticConfirm = async () => {
      const effectiveAdminSignature = myProfileSignature || adminSignatureData;
      if (!effectiveAdminSignature) {
        setFormError("Primary witness / administering caregiver signature is required.");
        return;
      }
      if (!selectedWitnessId || !witnessSignatureData) {
        setFormError("Secondary witness caregiver and signature are required.");
        return;
      }

      const file = dataURLtoFile(effectiveAdminSignature, 'signature.png');

      const selectedCaregiver = otherCaregivers.find(c => {
        const cid = String(c.id || c.keycloakId || (c as any).userId || '');
        return cid === selectedWitnessId || (c.id && String(c.id) === selectedWitnessId) || (c.keycloakId && String(c.keycloakId) === selectedWitnessId);
      });
      const resolvedWitnessUserId = selectedWitnessId || (selectedCaregiver ? String(selectedCaregiver.id || selectedCaregiver.keycloakId || '') : null);

      const cleanWitnessSig = witnessSignatureData
        ? (witnessSignatureData.includes(',') ? witnessSignatureData.split(',')[1] : witnessSignatureData)
        : null;

      const witnessPayload = resolvedWitnessUserId ? {
        witnessUserId: resolvedWitnessUserId,
        witnessId: resolvedWitnessUserId,
        witnessSignatureBase64: cleanWitnessSig,
        witnessSignature: cleanWitnessSig,
        witnessReason: "WITNESSED_ADMINISTRATION"
      } : undefined;

      setNarcoticDialogOpen(false);

      try {
        await submitRecord('Taken', file, effectiveAdminSignature, witnessPayload);
      } finally {
        setAdminSignatureData(null);
        setAdminSignatureMode(null);
        setWitnessSignatureData(null);
        setWitnessSignatureMode(null);
        setSelectedWitnessId('');
      }
    };

    const submitRecord = async (
      status: string,
      signatureFileArg?: Blob | File,
      signatureBase64Arg?: string | null,
      witnessData?: {
        witnessUserId?: string | null;
        witnessSignatureBase64?: string | null;
        witnessReason?: string | null;
        witnessId?: string | null;
        witnessSignature?: string | null;
      }
    ) => {
      if (!selectedCell) return;

      const isReasonReq = checkIsReasonMandatory(selectedCell.day, selectedCell.slot.slotTime, selectedCell.doseData?.dateTime);
      if ((isReasonReq || status === 'Unable' || status === 'Missed' || status === 'NotTaken') && !resultsResponse.trim()) {
        setFormError("Reason is mandatory for late or past-date medication administration.");
        return;
      }

      setIsSavingStatus(true);
      try {
        // Build dateTime using the selected day and the administrationTime from the dialog
        const [hours, minutes] = administrationTime.split(":").map(Number);
        const administrationDateTime = new Date(selectedCell.day);
        administrationDateTime.setHours(hours, minutes, 0, 0);

        const backendStatus =
          status === "Missed" || status === "NotTaken" ? "NotTaken" :
            status === "Out of Stock" || status === "OutOfMeds" || status === "OutOfStock" ? "OutOfStock" :
                status;

        // Ensure slotTime is in valid format like "07:00" (HH:mm)
        let formattedSlotTime = selectedCell.slot.slotTime || "";
        if (formattedSlotTime.includes("AM") || formattedSlotTime.includes("PM")) {
          try {
            const [time, period] = formattedSlotTime.split(" ");
            let [h, m] = time.split(":").map(Number);
            if (period === "PM" && h < 12) h += 12;
            if (period === "AM" && h === 12) h = 0;
            formattedSlotTime = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
          } catch (e) {
            console.error("Failed to parse slotTime", formattedSlotTime);
          }
        } else if (formattedSlotTime.includes(":")) {
          // If it has seconds, remove them to match HH:mm requirement
          formattedSlotTime = formattedSlotTime.split(':').slice(0, 2).join(':');
        }

        const checkNarcotic = (obj: any) => {
          if (!obj) return false;
          return Object.keys(obj).some(key => {
            const k = key.toLowerCase();
            return (k.includes('narcotic') || k.includes('norcatic')) &&
              (obj[key] === true || obj[key] === "true" || obj[key] === 1 || obj[key] === "1");
          });
        };

        const r = selectedCell.record as any || {};
        const d = (selectedCell.doseData as any) || {};

        const medName = (r.medicationName || "").toLowerCase().trim();

        const hasDirectFlag = !!(
          r.isNarcotic || r.isNorcatiik || r.isNorcatick || r.is_narcotic ||
          d.isNarcotic || d.isNorcatiik || d.isNorcatick || d.is_narcotic ||
          checkNarcotic(r) || checkNarcotic(d)
        );

        const isNarcoticByName = medicationDefinitions.some(m => {
          const mName = (m.medicationName || "").toLowerCase().trim();
          return mName === medName && (m.isNarcotic || m.isNorcatiik || m.isNorcatick || m.is_narcotic || checkNarcotic(m));
        });

        const isNarcoticMed = hasDirectFlag || isNarcoticByName;

        // Base64 handling for digital signatures (consistent with MedicationSchedulePage)
        const rawBase64 = signatureBase64Arg || signatureImage;
        const cleanBase64 = rawBase64
          ? (rawBase64.includes(',') ? rawBase64.split(',')[1] : rawBase64)
          : null;

        let effectiveSignatureFile: File | Blob | undefined = signatureFileArg || signatureFile || undefined;
        if (!effectiveSignatureFile && rawBase64) {
          effectiveSignatureFile = dataURLtoFile(rawBase64, 'signature.png');
        } else if (effectiveSignatureFile && !(effectiveSignatureFile instanceof File)) {
          effectiveSignatureFile = new File([effectiveSignatureFile], 'signature.png', {
            type: (effectiveSignatureFile as Blob).type || 'image/png'
          });
        }

        // Only narcotic medications send witness data; for non-narcotic or when no witness, they MUST be null
        const effectiveWitnessId = (isNarcoticMed && witnessData?.witnessUserId) ? witnessData.witnessUserId : null;
        const effectiveWitnessSig = (isNarcoticMed && effectiveWitnessId) ? (witnessData?.witnessSignatureBase64 || null) : null;
        const effectiveWitnessReason = (isNarcoticMed && effectiveWitnessId) ? (witnessData?.witnessReason || "WITNESSED_ADMINISTRATION") : null;

        const payload: any = {
          residentId: residentId,
          medicationName: selectedCell.record.medicationName,
          dosage: selectedCell.record.dose || "N/A",
          dateTime: format(administrationDateTime, "yyyy-MM-dd'T'HH:mm:ss"),
          slotTime: formattedSlotTime,
          medicationStatus: backendStatus,
          reason: resultsResponse.trim() || selectedCell.record.schedule || "Scheduled Dose",
          schedule: selectedCell.record.schedule || "Scheduled Dose",
          result: resultsResponse,
          lowStock: false,
          updatedStock: selectedCell.record.updatedStock ?? (selectedCell.record as any).updated_stock,
          updatedByName: user?.name || user?.username || "-",
          // Signature base64 fields sent to backend (matching MedicationSchedulePage)
          signature: cleanBase64,
          signatureBase64: cleanBase64,
          signatureData: cleanBase64,
          signatureString: cleanBase64,
          adminSignature: cleanBase64,
          adminSignatureBase64: cleanBase64,
          // Witness fields matching MedicationSchedulePage
          witnessUserId: effectiveWitnessId,
          witnessId: effectiveWitnessId,
          witnessSignatureBase64: effectiveWitnessSig,
          witnessSignature: effectiveWitnessSig,
          witnessReason: effectiveWitnessReason,
        };

        const noteIdToUpdate = selectedCell.doseData?.id || selectedCell.doseData?.apiId;
        let existingNoteId = noteIdToUpdate;

        if (!existingNoteId) {
          // Robust matching logic matching MedicationSchedulePage
          const targetTime = selectedCell.day;
          const targetSlot = formattedSlotTime;

          const existingNote = nurseNotes.find((n: any) => {
            if (!n.dateTime) return false;
            const d = new Date(n.dateTime);
            const isSameFullDate = d.getFullYear() === targetTime.getFullYear() &&
              d.getMonth() === targetTime.getMonth() &&
              d.getDate() === targetTime.getDate();

            if (n.medicationName !== selectedCell.record.medicationName) return false;
            if (!isSameFullDate) return false;

            // Priority 1: Match by slotTime (the best unique indicator for a medication on a given day)
            if (n.slotTime && targetSlot) {
              return n.slotTime.startsWith(targetSlot.substring(0, 5));
            }

            // Priority 2: fallback to approximate time if no slotTime
            return d.getHours() === administrationDateTime.getHours() &&
              d.getMinutes() === administrationDateTime.getMinutes();
          });
          existingNoteId = existingNote?.id;
        }

        if (existingNoteId) {
          await clinicalNoteService.updateNurseNote(existingNoteId, payload, effectiveSignatureFile);
        } else {
          await clinicalNoteService.createNurseNote(payload, effectiveSignatureFile);
        }

        const recordedMedName = selectedCell?.record?.medicationName || r.medicationName || "Medication";
        const formattedStatus = status === "Taken" ? "Taken" : status === "NotTaken" ? "Missed" : status;
        const successMessage = status === "Taken"
          ? `Medication "${recordedMedName}" marked as Taken successfully!`
          : `Medication "${recordedMedName}" status recorded as ${formattedStatus} successfully!`;

        setIsStatusDialogOpen(false);
        setSelectedCell(null);
        setResultsResponse("");
        resetSignatureState();

        setSnackbar({ open: true, message: successMessage, severity: "success" });
        await fetchData();
        if (onSaveSuccess) onSaveSuccess();
      } catch (error: any) {
        setIsSavingStatus(false);
        console.error("Failed to record medication status", error);

        // Handle Narcotic Signature Required Error from Backend
        const errorMsgRaw = error.response?.data?.message || "";
        const errorCode = error.response?.data?.errorCode || "";

        if (
          errorCode === "BUSINESS_VALIDATION_ERROR" &&
          (errorMsgRaw.toLowerCase().includes("signature") ||
            errorMsgRaw.toLowerCase().includes("narcotic") ||
            errorMsgRaw.toLowerCase().includes("sign"))
        ) {
          setPendingStatus(status);
          setFormError("Signature is mandatory for narcotic medications. Please sign below.");
          return;
        }

        const errorMsg = error.response?.data?.errorCode === "MEDICATION_OUT_OF_STOCK"
          ? "Stock Not Available For This Medication. (Contact Facility Manager)"
          : (error.response?.data?.message || error.message || "Failed to record status");
        setSnackbar({ open: true, message: errorMsg, severity: "error" });
      } finally {
        setIsSavingStatus(false);
      }
    };

    const daysInMonth = eachDayOfInterval({
      start: startOfMonth(currentDate),
      end: endOfMonth(currentDate),
    });

    // Helper to find a dose record for a specific slot and day
    const findDoseInSlot = (
      slot: MonthlyMARSlot,
      day: Date,
    ): MonthlyMARDose | undefined => {
      const dateStrISO = format(day, "yyyy-MM-dd");
      const dateStrUK = format(day, "dd/MM/yyyy");
      const dateStrUS = format(day, "MM/dd/yyyy");

      const found = slot.records.find((r: MonthlyMARDose) => {
        if (r.date === dateStrISO) return true;
        if (r.dateTime) {
          // Accept ISO format: "2026-01-03T07:00:00"
          if (r.dateTime.startsWith(dateStrISO)) return true;
          // Accept UK format: "03/01/2026 07:00"
          if (r.dateTime.startsWith(dateStrUK)) return true;
          // Accept US format: "01/03/2026 07:00"
          if (r.dateTime.startsWith(dateStrUS)) return true;
        }
        return false;
      });

      if (found) {
        const rawStatus = (found.status || "").toLowerCase();
        let mappedStatus = found.status;
        if (rawStatus === "pending") mappedStatus = "Upcoming";
        else if (rawStatus === "nottaken" || rawStatus === "not_taken" || rawStatus === "missed" || rawStatus === "not given" || rawStatus === "not_given") mappedStatus = "Missed";
        else if (rawStatus === "outofstock" || rawStatus === "out_of_stock" || rawStatus === "outofmeds") mappedStatus = "Out of Stock";
        else if (rawStatus === "taken") mappedStatus = "Taken";
        else if (rawStatus === "refused") mappedStatus = "Refused";
        else if (rawStatus === "discontinued") mappedStatus = "Discontinued";
        else if (rawStatus === "prn") mappedStatus = "PRN";
        else if (rawStatus === "unable") mappedStatus = "Unable";

        return {
          ...found,
          status: mappedStatus
        };
      }
      return undefined;
    };

    // Refined Icon Renderer matching MedicationSchedulePage
    const renderStatusIcon = (status: string, colorCode?: string) => {
      const boxStyle = {
        width: 28,
        height: 28,
        borderRadius: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        bgcolor: colorCode || "#546e7a", // Fallback color
      };

      switch (status) {
        case "Taken":
          return (
            <Box sx={{ ...boxStyle, bgcolor: "#F0FDF4", color: "#16A34A" }}>
              <CheckIcon fontSize="small" />
            </Box>
          );
        case "NotTaken":
        case "Missed":
          return (
            <Box sx={{ ...boxStyle, bgcolor: "#FEF2F2", color: "#DC2626" }}>
              <CloseIcon fontSize="small" sx={{ fontSize: 16 }} />
            </Box>
          );
        case "Not Given":
          return (
            <Box sx={{ ...boxStyle, bgcolor: "#FFF7ED", color: "#EA580C" }}>
              <CloseIcon fontSize="small" sx={{ fontSize: 16 }} />
            </Box>
          );
        case "Refused":
          return (
            <Box sx={{ ...boxStyle, bgcolor: "#E6F7F7", color: "#008C8C" }}>
              <Block fontSize="small" sx={{ fontSize: 16 }} />
            </Box>
          );
        case "Upcoming":
          return (
            <Box sx={{ ...boxStyle, bgcolor: "#F5F3FF", color: "#7C3AED" }}>
              <HistoryIcon fontSize="small" sx={{ fontSize: 16 }} />
            </Box>
          );
        case "OutOfMeds":
        case "Out of Stock":
        case "OutOfStock":
          return (
            <Box sx={{ ...boxStyle, bgcolor: "#F9FAFB", color: "#6B7280" }}>
              <InventoryIcon fontSize="small" sx={{ fontSize: 16 }} />
            </Box>
          );
        case "Discontinued":
          return (
            <Box sx={{ ...boxStyle, bgcolor: "#FEF3C7", color: "#78350F" }}>
              <RemoveCircleIcon fontSize="small" sx={{ fontSize: 16 }} />
            </Box>
          );
        case "PRN":
          return (
            <Box sx={{ ...boxStyle, bgcolor: "#E0F2FE", color: "#0077B6" }}>
              <MedicationIcon fontSize="small" sx={{ fontSize: 16 }} />
            </Box>
          );
        // Keep some backward compatibility mappings

        case "Unknown":
          return (
            <Box sx={{ ...boxStyle, bgcolor: "#6C757D" }}>
              <QuestionMark fontSize="small" sx={{ fontSize: 16 }} />
            </Box>
          );
        case "OutOfStock":
          return (
            <Box sx={{ ...boxStyle, bgcolor: "#6B7280" }}>
              <RemoveCircleIcon fontSize="small" sx={{ fontSize: 16 }} />
            </Box>
          );
        default:
          return <Box sx={boxStyle} />;
      }
    };

    // Determine max doses to show rows.
    // Format a slotTime string like "07:00" → "07:00 AM"
    const formatSlotTime = (slotTime: string): string => {
      if (!slotTime) return "";
      try {
        const [hours, minutes] = slotTime.split(":").map(Number);
        const d = new Date();
        d.setHours(hours, minutes || 0, 0, 0);
        return format(d, "hh:mm a");
      } catch {
        return slotTime;
      }
    };

    return (
      <>
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            overflow: "hidden",
            ...sx,
          }}
        >
          {/* Status Legend */}
          <Box
            sx={{
              p: 2,
              borderBottom: "1px solid #eee",
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              bgcolor: "white",
            }}
          >
            {[
              { label: "Taken", color: "#16A34A", bg: "#F0FDF4", border: "#BBF7D0" },
              { label: "Missed", color: "#DC2626", bg: "#FEF2F2", border: "#FECACA" },
              { label: "Not Given", color: "#EA580C", bg: "#FFF7ED", border: "#FED7AA" },
              { label: "Upcoming", color: "#7C3AED", bg: "#F5F3FF", border: "#DDD6FE" },
              { label: "Refused", color: "#008C8C", bg: "#E6F7F7", border: "#80cbc4" },
              { label: "Out of Stock", color: "#6B7280", bg: "#F9FAFB", border: "#E5E7EB" },
              { label: "Discontinued", color: "#78350F", bg: "#FEF3C7", border: "#FDE68A" },
              { label: "PRN", color: "#0077B6", bg: "#E0F2FE", border: "#BAE6FD" },
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  bgcolor: item.bg,
                  border: `1px solid ${item.border}`,
                  borderRadius: "16px",
                  px: 1.5,
                  py: 0.5,
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: item.color }} />
                <Typography variant="caption" sx={{ color: item.color, fontWeight: 600, fontSize: "0.75rem" }}>
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Header */}
          <Box
            sx={{
              bgcolor: "#48CAE4",
              color: "white",
              py: 2,
              px: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ fontSize: "1.1rem", letterSpacing: 0.5 }}
                >
                  MEDICATION ADMINISTRATION RECORD
                </Typography>
                {showResidentName && (
                  <Typography
                    variant="subtitle2"
                    sx={{ fontSize: "0.9rem", color: "white", fontWeight: 500 }}
                  >
                    Resident Name: {residentName}
                  </Typography>
                )}
              </Box>
              {/* Month navigation: < DatePicker > */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, bgcolor: "white", borderRadius: 1, pr: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => setCurrentDate(prev => subMonths(prev, 1))}
                  sx={{ color: "#555", "&:hover": { bgcolor: "rgba(0,0,0,0.08)" } }}
                >
                  <ChevronLeft fontSize="small" />
                </IconButton>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    views={["year", "month"]}
                    value={currentDate}
                    onChange={(newValue) => {
                      if (newValue) setCurrentDate(newValue);
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          "& fieldset": { border: "none" },
                          width: 150,
                          "& .MuiInputBase-input": { fontWeight: "bold", px: 0 },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
                <IconButton
                  size="small"
                  onClick={() => setCurrentDate(prev => addMonths(prev, 1))}
                  sx={{ color: "#555", "&:hover": { bgcolor: "rgba(0,0,0,0.08)" } }}
                >
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Box>
              {onClose && (
                <IconButton onClick={onClose} sx={{ color: "white", ml: 1 }}>
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Table */}
          <TableContainer
            sx={{
              maxHeight: "calc(100vh - 300px)",
              overflow: "auto",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                bgcolor: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "#888",
                borderRadius: "4px",
                "&:hover": {
                  bgcolor: "#555",
                },
              },
            }}
          >
            <Table size="small" sx={{ minWidth: 1500 }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      minWidth: 280,
                      borderRight: "1px solid #eee",
                      pl: 3,
                      py: 2,
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      bgcolor: "white",
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="text.secondary"
                    >
                      MEDICATION DETAILS
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      width: 100,
                      borderRight: "1px solid #eee",
                      textAlign: "center",
                      py: 2,
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      bgcolor: "white",
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="text.secondary"
                    >
                      HOUR
                    </Typography>
                  </TableCell>
                  {/* Days Header */}
                  <TableCell
                    colSpan={daysInMonth.length}
                    sx={{
                      p: 0,
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      bgcolor: "white",
                    }}
                  >
                    <Box sx={{ display: "flex", borderBottom: "1px solid #eee" }}>
                      <Box sx={{ width: "100%", textAlign: "center", py: 0.5 }}>
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          color="text.secondary"
                        >
                          ADMINISTRATION RECORD
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                      {daysInMonth.map((day) => (
                        <Box
                          key={day.toISOString()}
                          sx={{
                            flex: 1,
                            textAlign: "center",
                            borderRight: "1px solid #eee",
                            py: 0.5,
                            minWidth: 32,
                            bgcolor: "white",
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            {format(day, "d")}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={35} align="center" sx={{ py: 10 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : medicationRecords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={35} align="center" sx={{ py: 5 }}>
                      No medication records found for this month.
                    </TableCell>
                  </TableRow>
                ) : (
                  medicationRecords.map((record, index) => {
                    // Each slot is a separate row (doseNumber + slotTime)
                    const slots: MonthlyMARSlot[] = record.doses || [];

                    // Helper to parse various date formats safely
                    const parseNoteDate = (dateStr: any) => {
                      if (!dateStr) return 0;
                      const d = new Date(dateStr);
                      if (!isNaN(d.getTime())) return d.getTime();

                      // Handle DD/MM/YYYY
                      const match = String(dateStr).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
                      if (match) {
                        return new Date(parseInt(match[3]), parseInt(match[2]) - 1, parseInt(match[1])).getTime();
                      }
                      return 0;
                    };

                    // Determine low-stock status from all records across all slots
                    const allLowStockStatuses = [
                      ...slots.flatMap(slot => slot.records.map(r => ({ date: r.dateTime || r.date || "", lowStock: r.lowStock }))),
                      ...nurseNotes
                        .filter(n => (n.medicationName === record.medicationName || n.medication === record.medicationName))
                        .map(n => ({ date: n.dateTime || n.noteDate || "", lowStock: n.lowStock }))
                    ].filter(item => item.lowStock !== undefined);

                    const latestStatus = allLowStockStatuses.sort((a, b) => parseNoteDate(b.date) - parseNoteDate(a.date))[0];
                    const isLowStock = latestStatus ? latestStatus.lowStock === true : false;

                    return (
                      <React.Fragment key={index}>
                        {slots.map((slot, doseIndex) => (
                          <TableRow key={`${record.medicationName}-${slot.doseNumber}-${slot.slotTime}`}>
                            {/* Medication Info Cell - Render only on first slot row */}
                            {doseIndex === 0 && (
                              <TableCell
                                rowSpan={slots.length || 1}
                                sx={{
                                  verticalAlign: "middle",
                                  borderRight: "1px solid #eee",
                                  pl: 3,
                                  py: 2,
                                  bgcolor: "#E1F5FE", // Light sky blue background
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    '&:hover .low-stock-icon': {
                                      opacity: 1,
                                      visibility: "visible"
                                    }
                                  }}
                                >
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                    sx={{ color: "#2c3e50", lineHeight: 1.2, display: "flex", alignItems: "center", gap: 1 }}
                                  >
                                    {record.medicationName}
                                    {isStatMedication(record) && (
                                      <Tooltip title="STAT Medication" arrow>
                                        <Box
                                          component="img"
                                          src="https://cdn-icons-png.flaticon.com/512/3791/3791036.png"
                                          alt="STAT dose"
                                          sx={{
                                            width: 12,
                                            height: 12,
                                            display: "block",
                                            filter: "invert(31%) sepia(86%) saturate(1637%) hue-rotate(174deg) brightness(97%) contrast(101%)",
                                            transition: "transform 0.2s ease-in-out",
                                            "&:hover": {
                                              transform: "scale(1.15)",
                                            },
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                    {isNarcoticMedication(record) && (
                                      <Tooltip title="Narcotic Medication" arrow>
                                        <Box
                                          component="img"
                                          src="/assets/narcotic_icon.png"
                                          alt="Narcotic"
                                          sx={{
                                            width: 18,
                                            height: 18,
                                            display: "block",
                                            transition: "transform 0.2s ease-in-out",
                                            "&:hover": {
                                              transform: "scale(1.15)",
                                            },
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                    {isCriticalMedication(record) && (
                                      <Tooltip title="Critical Medication" arrow>
                                        <Box
                                          component="img"
                                          src="/assets/critical1.png"
                                          alt="Critical"
                                          sx={{
                                            width: 24,
                                            height: 24,
                                            display: "block",
                                            transition: "transform 0.2s ease-in-out",
                                            "&:hover": {
                                              transform: "scale(1.15)",
                                            },
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                  </Typography>
                                  {(() => {
                                    const isOutOfStock = slots.some(s => {
                                      const dose = findDoseInSlot(s, new Date());
                                      return dose?.status === "Out of Stock" || dose?.status === "OutOfStock" || dose?.status === "OutOfMeds";
                                    });

                                    if (isOutOfStock) {
                                      return (
                                        <Tooltip title="Out of Stock">
                                          <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <img
                                              src="/assets/Low-Stock-logo.png"
                                              alt="Out of Stock"
                                              style={{ height: 24, width: "auto" }}
                                            />
                                          </Box>
                                        </Tooltip>
                                      );
                                    }

                                    return (
                                      <Tooltip title={isLowStock ? "Low Stock" : "Mark as Low Stock"}>
                                        <Box
                                          className="low-stock-icon"
                                          sx={{
                                            opacity: isLowStock ? 1 : 0,
                                            visibility: isLowStock ? "visible" : "hidden",
                                            transition: "all 0.3s ease",
                                            display: "flex",
                                            alignItems: "center"
                                          }}
                                        >
                                          <img
                                            src="/assets/Low-Stock-logo.png"
                                            alt="Low Stock"
                                            style={{
                                              height: 24,
                                              width: "auto",
                                              cursor: readOnly ? "default" : "pointer",
                                              filter: isLowStock ? "none" : "grayscale(100%) opacity(0.3)",
                                            }}
                                            onClick={() => !readOnly && handleLowStockToggle(record, !isLowStock)}
                                          />
                                        </Box>
                                      </Tooltip>
                                    );
                                  })()}
                                </Box>
                              </TableCell>
                            )}

                            {/* Time Cell - directly uses slotTime from backend */}
                            <TableCell
                              sx={{
                                borderRight: "1px solid #eee",
                                textAlign: "center",
                                bgcolor: "#E1F5FE",
                                color: "#000",
                                fontWeight: 600,
                                fontSize: "0.8rem",
                              }}
                            >
                              {formatSlotTime(slot.slotTime)}
                            </TableCell>

                            {/* Grid Cells */}
                            <TableCell colSpan={daysInMonth.length} sx={{ p: 0 }}>
                              <Box sx={{ display: "flex" }}>
                                {daysInMonth.map((day) => {
                                  const doseData = findDoseInSlot(slot, day);
                                  const isPastDay = isBefore(day, startOfDay(new Date()));
                                  const isFutureDay = isFuture(day) && !isToday(day);
                                  let isPastTime = false;
                                  if (isToday(day) && slot.slotTime) {
                                    try {
                                      const [hours, minutes] = slot.slotTime.split(":").map(Number);
                                      const slotDateTime = new Date();
                                      slotDateTime.setHours(hours, minutes || 0, 0, 0);
                                      isPastTime = slotDateTime < new Date();
                                    } catch (e) {
                                      // ignore
                                    }
                                  }

                                  // "Upcoming" from backend = not yet administered → treat as no real status
                                  const realStatus = doseData?.status && doseData.status !== "Upcoming"
                                    ? doseData.status
                                    : null;

                                  // Check if cell is a cross (outside medication schedule/date range or unadministered STAT)
                                  const isCross = !doseData || (isStatDoseSource(record) && !realStatus && !(isPastDay || isPastTime));
                                  // Only show status/tooltip if cell is NOT a cross and has doseData
                                  const hasStatus = !isCross && !!doseData;

                                  const showNoAccessTooltip = hasStatus && isPastDay && !canEditPast;
                                  const isScheduled = !!doseData;
                                  const isDisabled = !isScheduled || isFutureDay || doseData?.status === "Taken";

                                  return (
                                    <Tooltip
                                      key={day.toISOString()}
                                      disableHoverListener={!hasStatus}
                                      disableFocusListener={!hasStatus}
                                      disableTouchListener={!hasStatus}
                                      title={
                                        !hasStatus ? "" :
                                        showNoAccessTooltip ? (
                                          <Box sx={{ p: 0.5, maxWidth: 300 }}>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontWeight: 600,
                                                color: "#FFFFFF",
                                                fontSize: "0.82rem",
                                                lineHeight: 1.4,
                                                mb: doseData ? 0.75 : 0,
                                              }}
                                            >
                                              You do not have access to edit previous medication. Please contact your Facility Manager.
                                            </Typography>
                                            {doseData && (
                                              <Box sx={{ pt: 0.75, borderTop: "1px solid rgba(255,255,255,0.25)" }}>
                                                <Typography variant="caption" sx={{ fontWeight: "bold", display: "block" }}>
                                                  {record.medicationName}
                                                </Typography>
                                                {doseData.dateTime && (
                                                  <Typography variant="caption" sx={{ display: "block" }}>
                                                    Date: {doseData.dateTime}
                                                  </Typography>
                                                )}
                                                <Typography variant="caption" sx={{ display: "block" }}>
                                                  Response: {((isStatMedication(record) || isStatDoseSource(record)) && (!doseData.status || doseData.status === "Missed" || doseData.status === "NotTaken" || doseData.status === "Not Given")) ? "Not Given" : (doseData.status || "Missed")}
                                                </Typography>
                                                {doseData.administeredBy && (
                                                  <Typography variant="caption" sx={{ display: "block" }}>
                                                    Caregiver: {doseData.administeredBy}
                                                  </Typography>
                                                )}
                                              </Box>
                                            )}
                                          </Box>
                                        ) : doseData ? (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexDirection: "column",
                                              gap: 0.5,
                                              p: 0.25,
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              sx={{ fontWeight: "bold" }}
                                            >
                                              {record.medicationName}
                                            </Typography>
                                            {doseData.dateTime && (
                                              <Typography variant="caption">
                                                Date: {doseData.dateTime}
                                              </Typography>
                                            )}
                                            {doseData.date &&
                                              !doseData.dateTime && (
                                                <Typography variant="caption">
                                                  Date: {doseData.date}
                                                </Typography>
                                              )}
                                            <Typography variant="caption">
                                              Response: {(() => {
                                                const realStatus = doseData?.status && doseData.status !== "Upcoming" ? doseData.status : null;
                                                const isStat = isStatMedication(record) || isStatDoseSource(record);
                                                if (isStat) {
                                                  if (!realStatus || realStatus === "Missed" || realStatus === "NotTaken" || realStatus === "Not Given") {
                                                    return "Not Given";
                                                  }
                                                  return realStatus;
                                                }
                                                return realStatus || ((isPastDay || isPastTime) ? "Missed" : (doseData?.status || "Upcoming"));
                                              })()}
                                            </Typography>
                                            {doseData.administeredBy && (
                                              <Typography variant="caption">
                                                Caregiver:{" "}
                                                {doseData.administeredBy}
                                              </Typography>
                                            )}
                                            {doseData.administeredAt && (
                                              <Typography variant="caption">
                                                Time: {doseData.administeredAt}
                                              </Typography>
                                            )}
                                            {doseData.notes && (
                                              <Typography variant="caption">
                                                Notes: {doseData.notes}
                                              </Typography>
                                            )}
                                            {(doseData.updatedByName || doseData.createdByName) &&
                                              (doseData.updatedByName !== "-" || doseData.createdByName !== "-") && (
                                                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                  👤 Caregiver: {doseData.updatedByName || doseData.createdByName}
                                                </Typography>
                                              )}
                                          </Box>
                                        ) : ""
                                      }
                                      arrow
                                      placement="bottom"
                                      enterDelay={50}
                                      leaveDelay={100}
                                      componentsProps={{
                                        tooltip: {
                                          sx: {
                                            bgcolor: "rgba(0, 0, 0, 0.92)",
                                            color: "#fff",
                                            boxShadow:
                                              "0 4px 14px rgba(0,0,0,0.25)",
                                            fontSize: "0.75rem",
                                            padding: "8px 12px",
                                            maxWidth: 320,
                                          },
                                        },
                                      }}
                                    >
                                      <Box
                                        onClick={() => {
                                          if (showNoAccessTooltip) {
                                            return;
                                          }
                                          if (!isDisabled) {
                                            handleCellClick(record, slot, day, doseData);
                                          }
                                        }}
                                        sx={{
                                          flex: 1,
                                          minWidth: 32,
                                          height: 48,
                                          borderRight: "1px solid #f5f5f5",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          cursor: !hasStatus ? "default" : (showNoAccessTooltip ? "help" : (isDisabled ? "not-allowed" : "pointer")),
                                          transition: "all 0.2s",
                                          bgcolor: !hasStatus ? "transparent" : (isDisabled ? "rgba(0,0,0,0.04)" : "transparent"),
                                          ...(!hasStatus ? {} : {
                                            "&:hover": {
                                              bgcolor: showNoAccessTooltip ? "rgba(239, 68, 68, 0.08)" : (isDisabled ? "rgba(0,0,0,0.04)" : "rgba(72,202,228,0.08)"),
                                              "& .cell-border": {
                                                borderColor: isDisabled ? "#eceff1" : "#48CAE4",
                                                transform: isDisabled ? "none" : "scale(1.1)",
                                                boxShadow: isDisabled ? "none" : "0 0 8px rgba(72,202,228,0.2)",
                                              }
                                            },
                                          }),
                                        }}
                                      >
                                        {(() => {
                                          if (!doseData) {
                                            // Outside medication date range → grey cross
                                            return (
                                              <CloseIcon sx={{ fontSize: 18, color: "#9e9e9e" }} />
                                            );
                                          }
                                          if (realStatus) {
                                            // Has a real recorded status (Taken, Missed, Refused, etc.)
                                            return renderStatusIcon(realStatus, doseData.colorCode);
                                          }
                                          if (isPastDay || isPastTime) {
                                            // Scheduled, past, not administered → auto Missed
                                            return renderStatusIcon(isStatDoseSource(record) ? "Not Given" : "Missed");
                                          }
                                          // Scheduled, today or future, not yet administered → Upcoming
                                          // STAT dose medications should NOT appear as Upcoming
                                          if (isStatDoseSource(record)) {
                                            return (
                                              <CloseIcon sx={{ fontSize: 18, color: "#9e9e9e" }} />
                                            );
                                          }
                                          return renderStatusIcon("Upcoming");
                                        })()}
                                      </Box>
                                    </Tooltip>
                                  );
                                })}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}

                        {/* Details Row */}
                        <TableRow>
                          <TableCell
                            colSpan={2}
                            sx={{
                              borderRight: "1px solid #eee",
                              pl: 3,
                              py: 1,
                              bgcolor: "white",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                              }}
                            >
                              {isStatMedication(record) ? (
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: "0.85rem" }}
                                >
                                  <Box component="span" fontWeight="bold">
                                    Medication Date:
                                  </Box>{" "}
                                  {formatToUIDate(
                                    record.startDate ||
                                    (record as any).start ||
                                    (record as any).start_date ||
                                    (record as any).date ||
                                    (record as any).dateTime ||
                                    record.doses?.[0]?.records?.[0]?.dateTime
                                  )}
                                </Typography>
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: "0.85rem" }}
                                >
                                  <Box component="span" fontWeight="bold">
                                    Medication Start Date:
                                  </Box>{" "}
                                  {formatToUIDate(
                                    record.startDate ||
                                    (record as any).start ||
                                    (record as any).start_date
                                  )}{" "}
                                  <Box component="span" fontWeight="bold">
                                    {" "}
                                    - Refill Date:
                                  </Box>{" "}
                                  {formatToUIDate(
                                    record.endDate ||
                                    (record as any).end ||
                                    (record as any).end_date ||
                                    (record as any).nextRefillDate
                                  )}
                                </Typography>
                              )}
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.85rem", color: "#555" }}
                              >
                                <Box component="span" fontWeight="bold">Dose:</Box> {record.dose || "N/A"} |{" "}
                                <Box component="span" fontWeight="bold">Route:</Box>{" "}
                                {record.route || "N/A"}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.85rem", color: "#555" }}
                              >
                                <Box component="span" fontWeight="bold">Schedule:</Box> {record.schedule || "N/A"}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.85rem", color: "#555" }}
                              >
                                <Box component="span" fontWeight="bold">Stock:</Box> {record.updatedStock ?? (record as any).updated_stock ?? "N/A"}
                              </Typography>
                            </Box>
                          </TableCell>
                          {/* Empty Grid Cells for formatting continuity */}
                          <TableCell colSpan={daysInMonth.length} sx={{ p: 0 }}>
                            <Box sx={{ display: "flex" }}>
                              {daysInMonth.map((day) => (
                                <Box
                                  key={`empty-${day.toISOString()}`}
                                  sx={{
                                    flex: 1,
                                    minWidth: 32,
                                    height: 48, // Match height of dose row? Or smaller?
                                    // Image suggests detail row might be slightly shorter or same.
                                    // Let's use auto logic or standard height.
                                    borderRight: "1px solid #f5f5f5",
                                    bgcolor: "#fafafa", // Slight grey for empty area? Or white? Image looked white or empty.
                                  }}
                                />
                              ))}
                            </Box>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Footer Actions */}
          {onClose && (
            <Box
              sx={{
                p: 2,
                borderTop: "1px solid #eee",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  bgcolor: "#0f172a",
                  textTransform: "none",
                  fontWeight: "bold",
                  "&:hover": { bgcolor: "#1e293b" },
                }}
              >
                Close Record
              </Button>
            </Box>
          )}
          {/* Status Selection Dialog */}
          <Dialog
            open={isStatusDialogOpen}
            onClose={() => { if (!isSavingStatus) { setIsStatusDialogOpen(false); resetSignatureState(); } }}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: { borderRadius: 3, overflow: 'hidden' }
            }}
          >
            <DialogTitle sx={{
              bgcolor: '#48CAE4',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CheckCircleIcon />
                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                  Record Administration
                </Typography>
              </Box>
              <IconButton
                onClick={() => { setIsStatusDialogOpen(false); resetSignatureState(); }}
                size="small"
                sx={{ color: 'white' }}
                disabled={isSavingStatus}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
              {selectedCell && (
                <Box sx={{ p: 3 }}>
                  {/* Inline validation error – shown at top of dialog */}
                  {formError && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 2.5,
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: '0.88rem',
                      }}
                    >
                      {formError}
                    </Alert>
                  )}
                  {/* Medication Info Card */}
                  <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                      <Box sx={{ p: 1, bgcolor: 'rgba(72,202,228,0.1)', borderRadius: 1.5, color: '#48CAE4' }}>
                        <LocalPharmacyIcon />
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="#1e293b" sx={{ lineHeight: 1.2, mb: 0.5, display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                          {selectedCell.record.medicationName}
                          {isStatMedication(selectedCell.record) && (
                            <Box
                              component="img"
                              src="https://cdn-icons-png.flaticon.com/512/3791/3791036.png"
                              alt="STAT dose"
                              sx={{
                                width: 14,
                                height: 14,
                                display: "inline-block",
                                verticalAlign: "middle",
                                filter: "invert(31%) sepia(86%) saturate(1637%) hue-rotate(174deg) brightness(97%) contrast(101%)"
                              }}
                            />
                          )}
                          <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#64748b' }}>
                            | Caregiver: {user?.name || user?.username || 'N/A'}
                          </Typography>
                        </Typography>
                        <Typography variant="body2" color="#64748b" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <InfoIcon sx={{ fontSize: 14 }} /> {selectedCell.record.dose || 'N/A'} • {selectedCell.record.schedule || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EventIcon sx={{ fontSize: 18, color: '#64748b' }} />
                          <Typography variant="body2" fontWeight="medium" color="#334155">
                            {format(selectedCell.day, 'EEEE, MMM do')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <HistoryIcon sx={{ fontSize: 18, color: '#64748b' }} />
                          <TextField
                            type="time"
                            size="small"
                            value={administrationTime}
                            onChange={(e) => setAdministrationTime(e.target.value)}
                            sx={{
                              width: 120,
                              '& .MuiInputBase-input': { py: 0.5, fontSize: '0.85rem' },
                              '& fieldset': { border: 'none' },
                              bgcolor: '#f1f5f9',
                              borderRadius: 2
                            }}
                          />
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, bgcolor: '#f1f5f9', borderRadius: 10 }}>
                        <InventoryIcon sx={{ fontSize: 16, color: '#48CAE4' }} />
                        <Typography variant="caption" fontWeight="bold" color="#48CAE4">
                          Stock: {selectedCell.record.updatedStock ?? (selectedCell.record as any).updated_stock ?? "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>

                  <Typography variant="subtitle2" fontWeight="bold" color="#475569" sx={{ mb: 1.5, px: 0.5 }}>
                    Results / Response {
                      (selectedCell && (checkIsReasonMandatory(selectedCell.day, selectedCell.slot.slotTime, selectedCell.doseData?.dateTime) || dialogStatus === 'Missed' || dialogStatus === 'NotTaken' || dialogStatus === 'Unable')) && (
                        <span style={{ color: '#DC3545' }}>*</span>
                      )
                    }
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Enter any results or response notes from daily medication..."
                    value={resultsResponse}
                    onChange={(e) => {
                      setResultsResponse(e.target.value);
                      if (formError) setFormError(null);
                    }}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#fff',
                        fontSize: '0.9rem',
                        '&:hover fieldset': {
                          borderColor: '#48CAE4',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#48CAE4',
                        },
                      }
                    }}
                  />

                  <Typography variant="subtitle2" fontWeight="bold" color="#475569" sx={{ mb: 2, px: 0.5 }}>
                    Select Result Status
                  </Typography>

                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
                    {[
                      { label: 'Taken', value: 'Taken', color: '#28A745', icon: <CheckCircleIcon /> },
                      { label: 'Missed', value: 'NotTaken', color: '#DC3545', icon: <CancelCircleIcon /> },
                      { label: 'Refused', value: 'Refused', color: '#008C8C', icon: <Block /> },
                      { label: 'Out of Stock', value: 'OutOfStock', color: '#1e293b', icon: <InventoryIcon /> },
                      { label: 'Discontinued', value: 'Discontinued', color: '#795548', icon: <RemoveCircleIcon /> },
                      { label: 'PRN', value: 'PRN', color: '#0077B6', icon: <MedicationIcon /> },
                    ].map((status) => {
                      const currentStock = selectedCell.record.updatedStock ?? (selectedCell.record as any).updated_stock ?? "N/A";
                      const isStockAvailable = currentStock !== "N/A" && currentStock !== null && currentStock !== undefined && currentStock !== "" && Number(currentStock) > 0;

                      return (
                        <Button
                          key={status.value}
                          variant="outlined"
                          onClick={() => {
                            setDialogStatus(status.value);
                            handleRecordStatus(status.value);
                          }}
                          disabled={
                            isSavingStatus ||
                            (status.label === "Out of Stock" && isStockAvailable)
                          }
                          sx={{
                            py: 1.5,
                            px: 2,
                            borderRadius: 2,
                            border: `1.5px solid ${status.color}20`,
                            color: status.color,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            textTransform: 'none',
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: `${status.color}10`,
                              border: `1.5px solid ${status.color}`,
                              transform: 'translateY(-2px)',
                              boxShadow: `0 4px 12px ${status.color}15`,
                            }
                          }}
                        >
                          {status.icon}
                          <Typography variant="body2" fontWeight="bold">
                            {status.label}
                          </Typography>
                        </Button>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </DialogContent>

            {isSavingStatus && (
              <Box sx={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                bgcolor: 'rgba(255,255,255,0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}>
                <CircularProgress size={40} thickness={4} sx={{ color: '#48CAE4' }} />
              </Box>
            )}
          </Dialog>

          {/* ── Narcotic Administration Verification Dialog (Identical to MedicationSchedulePage) ── */}
          <Dialog
            open={narcoticDialogOpen}
            onClose={() => setNarcoticDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ bgcolor: '#48CAE4', color: 'white', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              <MedicationIcon />
              Narcotic Administration Verification
              <IconButton onClick={() => {
                setNarcoticDialogOpen(false);
              }} sx={{ ml: 'auto', color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
              <Grid container spacing={2}>
                {/* ── Column 1: Administering Caregiver Signature ── */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ border: '1px solid #e0f4fa', borderRadius: 2, p: 2, bgcolor: '#f9fdff', height: '100%' }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5, color: '#0077B6' }}>
                      Primary witness
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Signed as: <strong>{user?.name || user?.username}</strong>
                    </Typography>

                    {myProfileSignature ? (
                      <Box sx={{ border: '2px solid #48CAE4', borderRadius: 2, p: 2, bgcolor: '#f0fbff', mb: 2, textAlign: 'center' }}>
                        <img
                          src={myProfileSignature}
                          alt="My Signature"
                          style={{ maxHeight: 100, maxWidth: '100%', display: 'block', margin: '0 auto' }}
                        />
                        <Typography variant="caption" color="primary" fontWeight={600} sx={{ mt: 0.5, display: 'block' }}>
                          Using Profile Signature
                        </Typography>
                      </Box>
                    ) : adminSignatureData ? (
                      <Box sx={{ border: '2px dashed #48CAE4', borderRadius: 2, p: 2, bgcolor: '#f0fbff', mb: 2, textAlign: 'center' }}>
                        <img
                          src={adminSignatureData}
                          alt="Admin Signature"
                          style={{ maxHeight: 100, maxWidth: '100%', display: 'block', margin: '0 auto' }}
                        />
                        <Typography variant="caption" color="success.main" fontWeight={600} sx={{ mt: 0.5, display: 'block' }}>
                          ✓ Signature captured for this administration
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ mt: 1 }}
                          onClick={() => setMySignatureDialogOpen(true)}
                        >
                          Re-sign
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            ref={adminUploadRef}
                            onChange={(e) => handleFileUpload(e, setAdminSignatureData)}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            fullWidth
                            startIcon={<CloudUploadIcon />}
                            sx={{
                              bgcolor: "#48CAE4",
                              color: "#000000",
                              fontWeight: "normal",
                              "&:hover": { bgcolor: "#3ab3cc" }
                            }}
                            onClick={() => {
                              setAdminSignatureMode('upload');
                              adminUploadRef.current?.click();
                            }}
                          >
                            Upload Signature
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            fullWidth
                            startIcon={<EditIcon />}
                            sx={{
                              bgcolor: "#48CAE4",
                              color: "#000000",
                              fontWeight: "normal",
                              "&:hover": { bgcolor: "#3ab3cc" }
                            }}
                            onClick={() => {
                              setAdminSignatureMode('digital');
                              setMySignatureDialogOpen(true);
                            }}
                          >
                            Digital Signature
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* ── Column 2: Witness Caregiver ── */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ border: '1px solid #e0f4fa', borderRadius: 2, p: 2, bgcolor: '#f9fdff', height: '100%' }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5, color: '#0077B6' }}>
                      Secondary Witness
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Select another caregiver to witness this administration. They will provide their own signature.
                    </Typography>

                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>Select Witness Caregiver</InputLabel>
                      <Select
                        value={selectedWitnessId}
                        label="Select Witness Caregiver"
                        disabled={caregiversLoading}
                        onChange={(e) => {
                          setSelectedWitnessId(e.target.value);
                          setWitnessSignatureData(null);
                          setWitnessSignatureMode(null);
                        }}
                        endAdornment={
                          caregiversLoading ? (
                            <CircularProgress size={18} sx={{ mr: 2 }} />
                          ) : undefined
                        }
                      >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {caregiversLoading ? (
                          <MenuItem disabled>
                            <em>Loading caregivers...</em>
                          </MenuItem>
                        ) : otherCaregivers.length === 0 ? (
                          <MenuItem disabled>
                            <em>No other caregivers available</em>
                          </MenuItem>
                        ) : (
                          otherCaregivers.map((c) => {
                            const caregiverId = String(c.id || c.keycloakId || (c as any).userId || '');
                            const caregiverName = `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.fullName || c.username || 'Caregiver';
                            return (
                              <MenuItem key={caregiverId || c.username} value={caregiverId}>
                                {caregiverName}
                              </MenuItem>
                            );
                          })
                        )}
                      </Select>
                    </FormControl>

                    {selectedWitnessId && (
                      <Box>
                        {witnessSignatureData && (
                          <Box sx={{ border: '1px solid #c8e6c9', borderRadius: 1.5, p: 1.5, bgcolor: 'white', mb: 2, textAlign: 'center' }}>
                            <img
                              src={witnessSignatureData}
                              alt="Witness Signature"
                              style={{ maxHeight: 80, maxWidth: '100%', display: 'block', margin: '0 auto' }}
                            />
                            <Typography variant="caption" color="success.main" fontWeight={600} sx={{ mt: 0.5, display: 'block' }}>
                              ✓ Witness signature captured
                            </Typography>
                          </Box>
                        )}

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            ref={witnessUploadRef}
                            onChange={(e) => handleFileUpload(e, setWitnessSignatureData)}
                          />
                          {witnessSignatureData ? (
                            <Button
                              variant="text"
                              size="small"
                              startIcon={<RemoveCircleIcon sx={{ color: '#d32f2f' }} />}
                              sx={{
                                color: '#d32f2f',
                                fontWeight: 500,
                                alignSelf: 'flex-start',
                                textTransform: 'none',
                                '&:hover': { bgcolor: 'rgba(211,47,47,0.06)' }
                              }}
                              onClick={() => {
                                setWitnessSignatureData(null);
                                setWitnessSignatureMode(null);
                              }}
                            >
                              Remove
                            </Button>
                          ) : (
                            <>
                              <Button
                                variant="contained"
                                size="small"
                                fullWidth
                                startIcon={<CloudUploadIcon />}
                                sx={{
                                  bgcolor: "#48CAE4",
                                  color: "#000000",
                                  fontWeight: "normal",
                                  "&:hover": { bgcolor: "#3ab3cc" }
                                }}
                                onClick={() => {
                                  setWitnessSignatureMode('upload');
                                  witnessUploadRef.current?.click();
                                }}
                              >
                                Upload Signature
                              </Button>
                              <Button
                                variant="contained"
                                size="small"
                                fullWidth
                                startIcon={<EditIcon />}
                                sx={{
                                  bgcolor: "#48CAE4",
                                  color: "#000000",
                                  fontWeight: "normal",
                                  "&:hover": { bgcolor: "#3ab3cc" }
                                }}
                                onClick={() => {
                                  setWitnessSignatureMode('digital');
                                  setWitnessSignatureDialogOpen(true);
                                }}
                              >
                                Digital Signature
                              </Button>
                            </>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
              <Button onClick={() => {
                setNarcoticDialogOpen(false);
              }} disabled={isSavingStatus}>
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={
                  (!myProfileSignature && !adminSignatureData) ||
                  !selectedWitnessId ||
                  !witnessSignatureData ||
                  isSavingStatus
                }
                onClick={handleNarcoticConfirm}
                sx={{ bgcolor: '#28A745', '&:hover': { bgcolor: '#218838' } }}
              >
                {isSavingStatus
                  ? <CircularProgress size={18} sx={{ color: 'white', mr: 1 }} />
                  : <CheckCircleIcon sx={{ mr: 0.5, fontSize: 18 }} />}
                Confirm Administration
              </Button>
            </DialogActions>
          </Dialog>

          {/* Admin Caregiver — inline signature creation (when no profile signature) */}
          <SignatureInput
            open={mySignatureDialogOpen}
            onClose={() => setMySignatureDialogOpen(false)}
            onSave={(dataUrl) => {
              setAdminSignatureData(dataUrl);
              setMySignatureDialogOpen(false);
            }}
            title="Create Your Digital Signature"
            confirmMessage="Confirm your signature for this narcotic administration?"
            initialTab={0}
            hideTabs={true}
          />

          {/* Witness SignatureInput dialog (handles both Upload and Digital draw tabs) */}
          <SignatureInput
            open={witnessSignatureDialogOpen}
            onClose={() => setWitnessSignatureDialogOpen(false)}
            onSave={(dataUrl) => {
              setWitnessSignatureData(dataUrl);
              setWitnessSignatureDialogOpen(false);
            }}
            title="Witness Signature"
            confirmMessage="Confirm your witness signature for this narcotic administration?"
            initialTab={0}
            hideTabs={true}
          />
        </Paper>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          sx={{ zIndex: 9999999 }}
        >
          <Alert
            onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
            severity={snackbar.severity}
            sx={{
              width: "auto",
              maxWidth: 420,
              boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.25), 0 8px 10px -6px rgba(0, 0, 0, 0.15)",
              borderRadius: "10px",
              fontWeight: 600,
              fontSize: "0.875rem",
              alignItems: "center",
              ...(snackbar.severity === "success" && {
                bgcolor: "#F0FDF4 !important",
                color: "#166534",
                border: "1px solid #86EFAC",
                "& .MuiAlert-icon": { color: "#16A34A" },
                "& .MuiAlert-action": { color: "#166534" },
              }),
              ...(snackbar.severity === "error" && {
                bgcolor: "#FEF2F2 !important",
                color: "#991B1B",
                border: "1px solid #FCA5A5",
                "& .MuiAlert-icon": { color: "#DC2626" },
              }),
              ...(snackbar.severity === "warning" && {
                bgcolor: "#FFFBEB !important",
                color: "#92400E",
                border: "1px solid #FDE68A",
                "& .MuiAlert-icon": { color: "#D97706" },
              }),
              ...(snackbar.severity === "info" && {
                bgcolor: "#EFF6FF !important",
                color: "#1E40AF",
                border: "1px solid #BFDBFE",
                "& .MuiAlert-icon": { color: "#2563EB" },
              }),
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
    );
  };

export default MedicationAdministrationRecord;